import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of } from 'rxjs';
import { authLog } from 'src/app/auth/auth.handler';
import {
  APP_DATETIME_FORMAT,
  APP_DATE_FORMAT,
  APP_TIME_FORMAT,
  IAppSettings,
  Language,
  SETTINGS,
  defaultLanguage,
  settings$,
  supportedLanguages,
} from 'src/app/core/contracts/index';
import { Logger } from 'src/app/core/helpers/logger';
import { getStorageItem, setStorageItem } from 'src/app/core/helpers/storage';
import { environment } from 'src/environments/environment';

@Injectable()
export class AppSettings {
  private translate = inject(TranslateService);
  private document = inject<Document>(DOCUMENT);
  private logger = inject(Logger);
  private defaultSettings: IAppSettings = {
    language: defaultLanguage,
    isFirstLaunch: true,
    dateFormat: APP_DATE_FORMAT,
    dateTimeFormat: APP_DATETIME_FORMAT,
    timeFormat: APP_TIME_FORMAT,
    version: environment.version, // added to allow purging outdated settings
  };
  private settingsPromise?: Promise<IAppSettings>;
  constructor() {
    this.init();
  }
  public init() {
    this.settingsPromise = getStorageItem<IAppSettings>(SETTINGS).then((savedSettings: IAppSettings | null) => {
      if (savedSettings) {
        // saved settings might be of an old version with missing properties
        if (savedSettings.version != environment.version) {
          // overwrite outdated settings with default values, but maintain first launch status
          savedSettings = { ...this.defaultSettings, isFirstLaunch: savedSettings.isFirstLaunch };
          return setStorageItem(SETTINGS, savedSettings).then(() => savedSettings);
        }
        return { ...this.defaultSettings, ...savedSettings };
      } else {
        return setStorageItem(SETTINGS, {
          ...this.defaultSettings,
          isFirstLaunch: false,
        }).then(() => this.defaultSettings);
      }
    });
    return this.settingsPromise;
  }
  public static isRtlLanguage(language: Language) {
    return language % 2 === 1;
  }
  public async initLanguage() {
    this.translate.setDefaultLang(Language[defaultLanguage]);
    const settings = await this.settingsPromise;
    if (settings?.isFirstLaunch) {
      settings.language = this.selectLanguage();
    }
    return firstValueFrom(this.setLanguage(settings))
      .then(() => {
        authLog(`auth.flow::=>settings.setLanguage: completed`);
      })
      .catch(err => {
        this.logger.error({ msg: '@settings: Failed to initialize language', err });
      });
  }
  public setLanguage(settings: IAppSettings | null | undefined) {
    if (!settings) {
      return of({});
    }
    this.saveSelectedLanguage(settings.language);
    settings$.next(settings);
    this.document.documentElement.dir = AppSettings.isRtlLanguage(settings.language) ? 'rtl' : 'ltr';
    this.document.documentElement.setAttribute('lang', Language[settings.language]);
    this.translate.use(Language[settings.language]).subscribe(); // TODO:investigate Hacky hotfix for initialization issue with translate.use not emitting
    return this.translate.use(Language[settings.language]);
  }
  private selectLanguage() {
    const userLanguage: string = navigator.language?.substring(0, 2).toLowerCase();
    const isUserLanguageSupported = supportedLanguages.some(lang => lang === userLanguage);
    const language: Language = isUserLanguageSupported ? Language[userLanguage as keyof typeof Language] : defaultLanguage;
    return language;
  }
  private async saveSelectedLanguage(language: Language) {
    const settings = await getStorageItem<IAppSettings>(SETTINGS);
    if (settings) {
      settings.language = language;
      setStorageItem(SETTINGS, settings);
    }
  }
}
